/**
 * P19 §3's account-deletion confirmation — **the same two independent gates `DeleteGateDialog`
 * puts in front of `DELETE /gates/{gateId}`, in front of the one call that is strictly worse.**
 *
 * `POST /privacy/erasure` destroys the profile, every membership and grant on it, every view
 * record, and pseudonymizes the history behind it. Nothing restores it: a return is a new
 * account, a new sign-in, and no gate. So the two controls are deliberately of *different
 * kinds*, so no single repeated gesture can carry someone through both:
 *
 *  1. **Type the account's own phone number.** Unlike the gate dialog's fixed word, this is a
 *     value the screen cannot show and only the account holder carries — so a phone handed to
 *     somebody else mid-session, or left unlocked, does not carry them through. **The exact
 *     comparison is the server's** (`packages/functions/privacy/erase.ts`, against the profile
 *     row): the client holds no copy of the number and must not start holding one for this.
 *     What is checked here is only that what was typed *is* a phone number, which is what the
 *     slider unlocks on — a shape test, never an identity one.
 *  2. **Carry the slider the length of its track.** Unlocked only once step 1 parses.
 *
 * **This is a deliberate escalation of P19 §3's own rule 1**, which named `ConfirmPopover` — a
 * yes/no. That reading was written before the gate dialog existed to compare against, and it
 * puts a *weaker* gate in front of erasure than the product already puts in front of deleting one
 * gate. The dialog family's split (D144) is by what each dialog *is*: this one asks with an
 * input, which is `Popover`'s shape rather than `ConfirmPopover`'s, and the gate dialog is the
 * shipped precedent for the input-plus-slider form. Recorded in `implementation-notes.md` → P19.
 *
 * **The last-manager guard is not in front of this call, and the copy says so.** D143 answers
 * `409 last-manager` on every ordinary path, and `packages/functions/privacy/erase.ts` says in
 * writing that a statutory erasure is not one of them — it proceeds and *reports* the gates left
 * without a manager. So there is nothing to pre-check (P19 §3 rule 2 is right about that much,
 * for a different reason than it gives) and the honest thing is a standing warning in the body
 * rather than a refusal that will never arrive.
 */
import { useState } from 'react'
import { normalizePhone } from '@selogate/core'
import { SecondaryButton, SlideToConfirm } from '../components'
import { useTranslation } from '../i18n/I18nProvider'

/**
 * Does this look like a phone number at all? **The shape, never the identity.**
 *
 * The same normalizer the sign-in screen and the route's own schema use, so a number this
 * refuses is one the server would have answered `422` for, and a number it accepts reaches the
 * one comparison that can actually settle it. A `false` here disables the slider; a mismatch
 * against the account comes back as `403 phone-mismatch` and renders as an error line.
 */
function looksLikeAPhoneNumber(typed: string): boolean {
  try {
    normalizePhone(typed, 'IL')
    return true
  } catch {
    return false
  }
}

export function DeleteAccountDialog({
  busy,
  error,
  onCancel,
  onEdit,
  onConfirm,
}: {
  readonly busy: boolean
  /** Rendered under the slider; its presence is the only thing that marks the attempt failed. */
  readonly error?: string
  onCancel(): void
  /**
   * The field was edited, so whatever {@link error} says is about a number that is no longer on
   * screen.
   *
   * **It is a callback rather than local state because the error is not this dialog's.** The
   * only failure it could clear by itself is one it raised, and it raises none: the shape test
   * disables the slider instead, and every sentence under the slider — `403 phone-mismatch`
   * included — is the server's, held by the caller. So the caller is the only thing that can
   * retract one.
   */
  onEdit?(): void
  /** Called with the number as typed; the caller sends it and the server canonicalizes it. */
  onConfirm(phone: string): void
}): JSX.Element {
  const { t } = useTranslation('account')
  const { t: tCommon } = useTranslation('common')
  const [typed, setTyped] = useState('')

  const parses = looksLikeAPhoneNumber(typed)

  return (
    <div className="popover-backdrop" onClick={busy ? undefined : onCancel}>
      <div
        className="popover"
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-account-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="popover-title" id="delete-account-title">
          {t('deleteTitle')}
        </h2>
        <p className="popover-body">{t('deleteBody')}</p>
        {/* The warning that has no server-side refusal behind it — see the docblock. It is
            unconditional rather than roster-driven: a pre-check would need a read this dialog
            does not make, and a resident whose network dropped must not be told they manage
            nothing. */}
        <p className="hint">{t('deleteManagerWarning')}</p>
        <label className="delete-gate-prompt" htmlFor="deleteAccountConfirm">
          {t('deletePhonePrompt')}
        </label>
        <input
          id="deleteAccountConfirm"
          className="input"
          type="tel"
          inputMode="tel"
          value={typed}
          onChange={(e) => {
            setTyped(e.target.value)
            onEdit?.()
          }}
          /* `autoComplete="off"` and not `tel`: the one field in the app where a suggestion
             chip carrying the right number would hand the confirmation to whoever is holding
             the phone, which is the gesture this dialog exists to make impossible. */
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          disabled={busy}
          aria-invalid={typed.length > 0 && !parses}
        />
        <SlideToConfirm
          label={t('deleteSlide')}
          committedLabel={t('deleteSliding')}
          disabled={!parses}
          busy={busy}
          onConfirm={() => onConfirm(typed)}
        />
        {error === undefined ? null : <p className="popover-error">{error}</p>}
        <div className="popover-actions">
          <SecondaryButton onClick={onCancel} disabled={busy}>
            {tCommon('cancel')}
          </SecondaryButton>
        </div>
      </div>
    </div>
  )
}
